import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  LinearProgress,
  Fab,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  EggAlt as EggIcon,
  Inventory2 as StockIcon,
  Paid as PaidIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useBrokenEggSales, useBrokenEggStock, useCreateBrokenEggSale } from '../hooks/useEggSales';
import { useCustomers } from '../hooks/useOthers';

const formInputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#111',
    color: '#e0e0e0',
    borderRadius: 1.5,
    '& fieldset': { borderColor: 'rgba(255,255,255,0.1)' },
    '&:hover fieldset': { borderColor: 'rgba(255,152,0,0.4)' },
    '&.Mui-focused fieldset': { borderColor: '#ff9800' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.4)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#ff9800' },
  '& .MuiSelect-icon': { color: '#666' },
};

const dataGridSx = {
  border: 'none',
  background: 'transparent',
  '& .MuiDataGrid-cell': {
    borderBottom: '1px solid rgba(255,255,255,0.04)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    '&:focus, &:focus-within': { outline: 'none' },
  },
  '& .MuiDataGrid-columnHeaders': {
    background: 'rgba(255,152,0,0.06)',
    borderBottom: '1px solid rgba(255,152,0,0.15)',
  },
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
    outline: 'none',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 700,
    fontSize: '0.68rem',
    letterSpacing: '0.7px',
    color: 'rgba(255,152,0,0.65)',
    textTransform: 'uppercase',
  },
  '& .MuiDataGrid-row:hover': { background: 'rgba(255,152,0,0.04)' },
  '& .MuiDataGrid-footerContainer': {
    borderTop: '1px solid rgba(255,152,0,0.1)',
    background: 'rgba(255,152,0,0.03)',
  },
  '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
    { color: 'rgba(255,255,255,0.45)', fontSize: '0.78rem' },
  '& .MuiIconButton-root': { color: 'rgba(255,255,255,0.35)' },
  '& .MuiIconButton-root.Mui-disabled': { color: 'rgba(255,255,255,0.12)' },
  '& .MuiDataGrid-virtualScroller': { minHeight: 200 },
};

const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  sub?: string;
  accent: string;
  Icon: React.ElementType;
}> = ({ label, value, sub, accent, Icon }) => (
  <Paper
    elevation={0}
    sx={{
      flex: 1,
      minWidth: 160,
      p: 2.5,
      background: '#111318',
      border: '1px solid rgba(255,255,255,0.05)',
      borderLeft: `3px solid ${accent}`,
      borderRadius: 2.5,
      position: 'relative',
      overflow: 'hidden',
      boxShadow: `inset 0 1px 0 ${accent}22`,
    }}
  >
    <Icon
      sx={{
        position: 'absolute',
        right: 12,
        top: '50%',
        transform: 'translateY(-50%)',
        fontSize: 56,
        color: `${accent}0C`,
      }}
    />
    <Typography
      sx={{
        color: 'rgba(255,255,255,0.35)',
        letterSpacing: '0.7px',
        fontSize: '0.62rem',
        fontWeight: 700,
        textTransform: 'uppercase',
        mb: 0.75,
      }}
    >
      {label}
    </Typography>
    <Typography fontWeight={800} sx={{ color: accent, lineHeight: 1, mb: 0.5, fontSize: '1.8rem' }}>
      {value}
    </Typography>
    {sub && (
      <Typography sx={{ color: 'rgba(255,255,255,0.28)', fontSize: '0.65rem' }}>{sub}</Typography>
    )}
  </Paper>
);

const EMPTY_FORM = {
  customer_id: '',
  crate_quantity: '',
  unit_price: '',
  amount_paid: '',
  sales_date: new Date().toISOString().split('T')[0],
};

const BrokenEggSalesPage: React.FC = () => {
  const [addOpen, setAddOpen] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

  const { data: salesData, isFetching } = useBrokenEggSales({
    page: paginationModel.page + 1,
    per_page: paginationModel.pageSize,
  });
  const { data: stock } = useBrokenEggStock();
  const { data: customersData } = useCustomers({ per_page: 200 });
  const createMutation = useCreateBrokenEggSale();

  const sales = salesData?.data || [];
  const total = salesData?.total || 0;
  const customers = customersData?.data || [];

  const crateQty = parseInt(formData.crate_quantity) || 0;
  const unitPrice = parseInt(formData.unit_price) || 0;
  const totalEggs = crateQty * 30;
  const totalPrice = crateQty * unitPrice;
  const amountPaid = parseInt(formData.amount_paid) || 0;
  const amountLeft = totalPrice - amountPaid;

  const handleSubmit = async () => {
    setFormError('');
    if (!formData.customer_id || !formData.crate_quantity || !formData.unit_price) {
      setFormError('Customer, crate quantity and unit price are required.');
      return;
    }
    if (stock && totalEggs > stock.available) {
      setFormError(`Only ${stock.available_crates} crates available.`);
      return;
    }
    try {
      await createMutation.mutateAsync({
        customer_id: Number(formData.customer_id),
        crate_quantity: crateQty,
        unit_price: unitPrice,
        amount_paid: amountPaid,
        sales_date: formData.sales_date,
      });
      setAddOpen(false);
      setFormData(EMPTY_FORM);
    } catch (e: any) {
      setFormError(e.response?.data?.message || 'Failed to record sale');
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'sales_date',
      headerName: 'Date',
      width: 120,
      renderCell: ({ value }: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: '#e0e0e0' }}>
          {new Date(value).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
          })}
        </Typography>
      ),
    },
    {
      field: 'customer',
      headerName: 'Customer',
      flex: 1,
      renderCell: ({ row }: GridRenderCellParams) => (
        <Typography variant="body2" sx={{ color: '#e0e0e0', fontWeight: 500 }}>
          {row.customer?.name || '—'}
        </Typography>
      ),
    },
    {
      field: 'crate_quantity',
      headerName: 'Crates',
      width: 100,
      renderCell: ({ row }: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{ color: 'rgba(255,255,255,0.7)', fontVariantNumeric: 'tabular-nums' }}
        >
          {row.crate_quantity}
        </Typography>
      ),
    },
    {
      field: 'unit_price',
      headerName: 'Price / Crate',
      width: 130,
      renderCell: ({ value }: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{ color: 'rgba(255,255,255,0.7)', fontVariantNumeric: 'tabular-nums' }}
        >
          {value}
        </Typography>
      ),
    },
    {
      field: 'total_price',
      headerName: 'Total',
      width: 120,
      renderCell: ({ value }: GridRenderCellParams) => (
        <Typography
          variant="body2"
          sx={{ color: '#ff9800', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}
        >
          {value}
        </Typography>
      ),
    },
    {
      field: 'is_paid',
      headerName: 'Payment',
      width: 130,
      renderCell: ({ row }: GridRenderCellParams) => (
        <Chip
          size="small"
          label={row.is_paid ? 'Paid' : `Owes ${row.amount_left}`}
          sx={{
            bgcolor: row.is_paid ? 'rgba(76,175,80,0.15)' : 'rgba(244,67,54,0.12)',
            color: row.is_paid ? '#81c784' : '#ef9a9a',
            fontWeight: 600,
            fontSize: '0.7rem',
          }}
        />
      ),
    },
    {
      field: 'sold_by',
      headerName: 'Sold by',
      width: 130,
      renderCell: ({ row }: GridRenderCellParams) => (
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
          {row.sold_by?.name || '—'}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            p: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #1a1200 0%, #0d0d0d 100%)',
            border: '1px solid rgba(255,152,0,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: 'rgba(255,152,0,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EggIcon sx={{ color: '#ff9800', fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
                Broken Egg Sales
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.4)', mt: 0.3 }}>
                Track sales of broken eggs from production
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
            sx={{
              bgcolor: '#ff9800',
              color: '#000',
              fontWeight: 700,
              '&:hover': { bgcolor: '#ffa726' },
            }}
          >
            Record Sale
          </Button>
        </Box>
      </Box>

      {/* Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard
          label="Available Stock"
          value={stock ? `${stock.available_crates} crates` : '—'}
          sub={undefined}
          accent="#ff9800"
          Icon={StockIcon}
        />
        <StatCard
          label="Total Produced"
          value={stock ? stock.total_produced.toLocaleString() : '—'}
          sub="eggs from all production records"
          accent="#42a5f5"
          Icon={EggIcon}
        />
        <StatCard
          label="Total Sold"
          value={stock ? stock.total_sold.toLocaleString() : '—'}
          sub="eggs sold to date"
          accent="#66bb6a"
          Icon={PaidIcon}
        />
        <StatCard label="Sale Records" value={total} accent="#ab47bc" Icon={PaidIcon} />
      </Box>

      {/* Stock warning */}
      {stock && stock.available < 30 && (
        <Box
          sx={{
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'rgba(255,152,0,0.08)',
            border: '1px solid rgba(255,152,0,0.2)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <WarningIcon sx={{ color: '#ff9800', fontSize: 18 }} />
          <Typography variant="body2" sx={{ color: 'rgba(255,152,0,0.9)' }}>
            Less than one crate of broken eggs available in stock.
          </Typography>
        </Box>
      )}

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          background: '#0e1015',
          border: '1px solid rgba(255,255,255,0.06)',
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        {isFetching && <LinearProgress sx={{ height: 2 }} />}
        <DataGrid
          rows={sales}
          columns={columns}
          rowCount={total}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[25, 50, 100]}
          disableRowSelectionOnClick
          sx={dataGridSx}
          autoHeight
        />
      </Paper>

      {/* Add sale dialog */}
      <Dialog
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          setFormError('');
          setFormData(EMPTY_FORM);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { bgcolor: '#0e1015', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 3 },
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#fff' }}>
            Record Broken Egg Sale
          </Typography>
          {stock && (
            <Typography variant="body2" sx={{ color: 'rgba(255,152,0,0.7)', mt: 0.5 }}>
              {stock.available_crates} crates available
            </Typography>
          )}
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Box display="flex" flexDirection="column" gap={2.5}>
            <TextField
              select
              required
              fullWidth
              size="small"
              label="Customer"
              value={formData.customer_id}
              onChange={(e) => setFormData({ ...formData, customer_id: String(e.target.value) })}
              sx={formInputSx}
            >
              {customers.map((c: any) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                required
                fullWidth
                size="small"
                type="number"
                label="Crate Quantity"
                value={formData.crate_quantity}
                onChange={(e) => setFormData({ ...formData, crate_quantity: e.target.value })}
                slotProps={{ htmlInput: { min: 1 } }}
                sx={formInputSx}
              />
            </Box>

            {crateQty > 0 && (
              <Typography variant="caption" sx={{ color: 'rgba(255,152,0,0.6)', mt: -1 }}>
                Total: {totalEggs.toLocaleString()} eggs ({crateQty} crates)
              </Typography>
            )}

            <TextField
              required
              fullWidth
              size="small"
              type="number"
              label="Unit Price per Crate (GH₵)"
              value={formData.unit_price}
              onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
              slotProps={{ htmlInput: { min: 1 } }}
              sx={formInputSx}
            />

            <TextField
              fullWidth
              size="small"
              type="number"
              label="Amount Paid (GH₵)"
              value={formData.amount_paid}
              onChange={(e) => setFormData({ ...formData, amount_paid: e.target.value })}
              slotProps={{ htmlInput: { min: 0 } }}
              helperText={
                crateQty > 0 && unitPrice > 0
                  ? `Total: GH₵ ${totalPrice.toFixed(2)} · Owing: GH₵ ${Math.max(0, amountLeft).toFixed(2)}`
                  : undefined
              }
              FormHelperTextProps={{ sx: { color: 'rgba(255,255,255,0.3)', fontSize: '0.68rem' } }}
              sx={formInputSx}
            />

            <TextField
              required
              fullWidth
              size="small"
              type="date"
              label="Sale Date"
              value={formData.sales_date}
              onChange={(e) => setFormData({ ...formData, sales_date: e.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={formInputSx}
            />

            {formError && (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(244,67,54,0.08)',
                  border: '1px solid rgba(244,67,54,0.2)',
                }}
              >
                <Typography variant="body2" sx={{ color: '#ef9a9a' }}>
                  {formError}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, pt: 0 }}>
          <Button
            onClick={() => {
              setAddOpen(false);
              setFormError('');
              setFormData(EMPTY_FORM);
            }}
            sx={{ color: 'rgba(255,255,255,0.4)' }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isPending}
            sx={{
              bgcolor: '#ff9800',
              color: '#000',
              fontWeight: 700,
              '&:hover': { bgcolor: '#ffa726' },
            }}
          >
            {createMutation.isPending ? 'Saving...' : 'Record Sale'}
          </Button>
        </DialogActions>
      </Dialog>

      <Fab
        color="primary"
        onClick={() => setAddOpen(true)}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          bgcolor: '#ff9800',
          color: '#000',
          display: { xs: 'flex', md: 'none' },
          '&:hover': { bgcolor: '#ffa726' },
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default BrokenEggSalesPage;
